import React from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Waveform from "@/components/Waveform";

/**
 * Controlled recorder surface — the page-body mic + waveform strip.
 * Recording state is owned by a parent `useRecorder()` so the mobile
 * bottom bar can share the same session.
 *
 * Mobile-first sizing: the mic/stop button grows to h-20 w-20 (80px) on
 * small screens so it's easy to hit one-handed, then drops back to h-14
 * (56px) on ≥sm where the pointer is a mouse. Waveform stacks BELOW the
 * button on mobile so the whole strip breathes; side-by-side on ≥sm to
 * preserve the editorial layout.
 */
export default function VoiceRecorder({
  promptId,
  recording,
  transcribing,
  onStart,
  onStop,
}) {
  const busy = transcribing;
  const isActive = recording || transcribing;
  return (
    <div className="flex flex-col-reverse sm:flex-row items-center gap-5 sm:gap-6">
      <div className="flex-1 w-full">
        <Waveform active={recording} />
        {recording && (
          <p
            data-testid={`record-status-${promptId}`}
            className="mt-2 text-center sm:text-left font-sans text-[0.65rem] uppercase tracking-[0.24em] text-terracotta animate-breathe"
          >
            Recording · tap the stop button when you're ready
          </p>
        )}
        {transcribing && !recording && (
          <p className="mt-2 text-center sm:text-left font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-400">
            Transcribing…
          </p>
        )}
      </div>
      {!recording ? (
        <Button
          data-testid={`record-btn-${promptId}`}
          onClick={onStart}
          disabled={busy}
          aria-label={busy ? "Transcribing your recording" : "Start recording"}
          className="rounded-full bg-terracotta hover:bg-terracotta-soft text-bone-100 h-20 w-20 sm:h-14 sm:w-14 p-0 shadow-[0_0_0_1px_rgba(184,90,68,0.35)] transition-colors flex-shrink-0"
        >
          {busy ? <Loader2 className="h-7 w-7 sm:h-5 sm:w-5 animate-spin" /> : <Mic className="h-7 w-7 sm:h-5 sm:w-5" />}
        </Button>
      ) : (
        <Button
          data-testid={`stop-btn-${promptId}`}
          onClick={onStop}
          aria-label="Stop recording and transcribe"
          className="rounded-full bg-terracotta hover:bg-terracotta-deep text-bone-100 h-20 w-20 sm:h-14 sm:w-14 p-0 animate-breathe transition-colors flex-shrink-0 shadow-[0_0_0_2px_rgba(184,90,68,0.35)]"
        >
          <Square className="h-6 w-6 sm:h-4 sm:w-4 fill-current" />
        </Button>
      )}
    </div>
  );
}
